import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import {
  MANAGE_ORG_MODULES_ACCESS,
  MODULES_ADMIN_MODULE_KEY,
} from '../constants/org-modules-admin';
import { CustomNotificationService } from './custom.notification.service';
import { OrgModulesService } from './org-modules.service';
import { PermissionService } from './permissions.service';

export type OrgModulesAdminDenial = 'module' | 'access';

/**
 * Regla única de visibilidad de «Modules», compartida por este guard y la entrada del menú
 * (`HeaderUserComponent.canManageOrgModules`): qué falta para entrar (el módulo `modules-admin` en `orgModules`,
 * el access `manage_org_modules`) o `null` si no falta nada.
 */
export function orgModulesAdminDenial(
  orgModules: Pick<OrgModulesService, 'hasAccessToModule'>,
  permissions: Pick<PermissionService, 'hasPermission'>,
): OrgModulesAdminDenial | null {
  if (!orgModules.hasAccessToModule(MODULES_ADMIN_MODULE_KEY)) return 'module';
  if (!permissions.hasPermission(MANAGE_ORG_MODULES_ACCESS)) return 'access';
  return null;
}

/** Texto del aviso «Sin acceso» según lo que falte (mismo patrón que `AgentsEngineModuleGuard`). */
export const ORG_MODULES_ADMIN_DENIAL_MESSAGES: Readonly<Record<OrgModulesAdminDenial, string>> = {
  module: 'Tu organización no tiene activo el administrador de módulos.',
  access:
    'Tu usuario no tiene el acceso para administrar los módulos de la organización (manage_org_modules). Pide a un administrador que te lo asigne y vuelve a entrar.',
};

/**
 * Protege `/pages/org-modules` («Módulos por organización», herramienta de plataforma): la organización actual debe
 * tener activo el módulo `modules-admin` (en el JWT como `orgModules`) y el usuario el access `manage_org_modules`
 * (misma condición que la entrada del menú, `HeaderUserComponent.canManageOrgModules`). Sin ello: aviso
 * «Sin acceso» y vuelta al dashboard. La autoridad es la API, que vuelve a comprobar ambas cosas en BD en cada
 * petición.
 */
@Injectable({
  providedIn: 'root',
})
export class OrgModulesAdminGuard implements CanActivate {
  constructor(
    private readonly orgModulesService: OrgModulesService,
    private readonly permissionService: PermissionService,
    private readonly router: Router,
    private readonly notification: CustomNotificationService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const denial = this.denial();
    if (!denial) {
      return true;
    }

    this.notification.show(
      'warning',
      'Sin acceso',
      ORG_MODULES_ADMIN_DENIAL_MESSAGES[denial],
      'topRight',
    );
    this.router.navigate(['/pages/dashboard'], {
      queryParams: { returnUrl: state.url },
    });
    return false;
  }

  /** Qué falta para entrar: el módulo `modules-admin`, el access, o nada (`null`). */
  private denial(): OrgModulesAdminDenial | null {
    return orgModulesAdminDenial(this.orgModulesService, this.permissionService);
  }
}
